import { useMemo } from 'react';
import { signupStepLoaded } from '@hotjar/tracking';
import { useTranslation } from 'react-i18next';
import { useDeviceType } from '@hotjar/webapp/hooks/useDeviceType';
import type { RegistrationCampaign } from '@hotjar/webapp/domains/campaigns/model';
import type { Site } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';

interface OnboardingTracking {
  trackLogin: () => void;
  trackFormSubmitted: (success: boolean) => void;
  trackSignupStepLoaded: (campaign?: RegistrationCampaign) => void;
  trackSignupStepSubmitted: (campaign?: RegistrationCampaign) => void;
  trackSignupStepCompleted: (campaign?: RegistrationCampaign) => void;
  trackHotjarInstallerOpened: (site: Site) => void;
  trackOnboardingStarted: (site: Site) => void;
}

export const useOnboardingTracking = (): OnboardingTracking => {
  const track = useTrack();
  const { i18n } = useTranslation();
  const signupMethod = useQueryParameter('signup_method');
  const { deviceType } = useDeviceType();
  const currentLocale = i18n.languages?.[0];

  const trackingData = useMemo(
    () => ({
      signupMethod,
      signupStep: 'organization-details',
      signupStepNumber: 3,
      ...(currentLocale ? { locale: currentLocale } : {}),
    }),
    [signupMethod, currentLocale],
  );

  const trackSignupStepLoaded = useMemo(
    () => (campaign?: RegistrationCampaign) =>
      signupStepLoaded({ ...trackingData, campaign, deviceType }),
    [trackingData], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    trackLogin: () => track.login(),
    trackOnboardingStarted: (site: Site) => track.onboardingStarted(site),
    trackFormSubmitted: (success = true) =>
      track.formSubmit(
        'Account signup',
        success,
        {},
        !success ? { app_form_submit_failure_reason: 'Unhandled Error' } : undefined,
      ),
    trackSignupStepLoaded,
    trackSignupStepSubmitted: (campaign?: RegistrationCampaign) =>
      track.signupStepSubmitted({ ...trackingData, campaign }),
    trackSignupStepCompleted: (campaign?: RegistrationCampaign) =>
      track.signupStepCompleted({ ...trackingData, campaign }),
    trackHotjarInstallerOpened: (site: Site) =>
      track.hotjarInstallerOpened(site, 'Sign Up', 'install'),
  };
};
